import React from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Dialog,
  HStack,
  Icon,
  Portal,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { yupResolver } from '@hookform/resolvers/yup';
import { ReactComponent as Edit } from 'assets/icons/edit.svg';
import { useUpdatePassword } from 'hooks';
import useInputAttributes from 'hooks/useInputAttributes';
import { useTranslation } from 'localization';

import {
  Button,
  CopyClipBoard,
  DIALOG_ACTION_BUTTON_PROPS,
  Input,
  PasswordStrength,
  ServiceDialogBody,
  ServiceDialogCloseTrigger,
  ServiceDialogFooter,
  ServiceDialogHeader,
  serviceDialogShellProps,
} from 'components';
import { passwordRegex, yup } from 'utils';

import BorderedBox from '../BorderedBox';

type PayloadType = { old_password: string; password: string };

const AccountDetail: React.FC<{
  title: string;
  list: {
    label: string;
    value: any;
    isEditable?: boolean;
    isHide?: boolean;
    isCopyable: boolean;
  }[];
}> = ({ title, list }) => {
  const { open, onOpen, onClose } = useDisclosure();
  const { t } = useTranslation('component.settings.profile.updatePassword');

  const { mutateAsync: changePassword, isPending: isPasswordSubmitting } =
    useUpdatePassword();

  const schema = yup.object().shape({
    old_password: yup.string().label(t('form.old_password')).required(),
    password: yup
      .string()
      .label(t('form.password'))
      .matches(passwordRegex)
      .required(),
    confirm_password: yup
      .string()
      .label(t('form.confirm_password'))
      .oneOf([yup.ref('password')], t('form.same_password'))
      .required(),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      old_password: '',
      password: '',
      confirm_password: '',
    },
  });

  const onSubmit = async (data: PayloadType & { confirm_password: string }) => {
    changePassword({
      password: data.password,
      old_password: data.old_password,
    }).then(() => {
      onClose();
      reset();
    });
  };

  const formProps = useInputAttributes(
    register,
    errors,
    {
      old_password: t('form.old_password'),
      password: t('form.password'),
      confirm_password: t('form.confirm_password'),
    },
    ['password']
  );

  return (
    <BorderedBox title={title}>
      <Stack gap={0}>
        {list
          .filter((item) => !item.isHide)
          .map((item, index, arr) => {
            return (
              <Stack
                key={item.label}
                w="100%"
                alignItems={{ base: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                flexDir="row"
                py={3}
                gap={3}
                borderBottomWidth={index < arr.length - 1 ? '1px' : 0}
                borderBottomColor="rgba(255,255,255,0.06)"
              >
                <Stack
                  gap={{ base: 1, md: 2 }}
                  flexDir={{ base: 'column', md: 'row' }}
                  align={{ base: 'flex-start', md: 'center' }}
                  minW={0}
                  flex="1"
                >
                  <Text
                    fontSize="sm"
                    fontWeight="medium"
                    color="body2"
                    pr={{ md: 2 }}
                    minW={{ md: '140px' }}
                  >
                    {item.label}
                  </Text>
                  {item.isCopyable ? (
                    <CopyClipBoard
                      initialValue={item.value?.toString() || ''}
                    />
                  ) : (
                    <Text wordBreak="break-word" fontSize="sm" color="body">
                      {item.value}
                    </Text>
                  )}
                </Stack>
                {item.isEditable && (
                  <Box
                    role="button"
                    tabIndex={0}
                    aria-label={t('form.title')}
                    cursor="pointer"
                    p={2}
                    borderRadius="md"
                    flexShrink={0}
                    transition="background 0.15s ease"
                    _hover={{ bg: 'rgba(255, 255, 255, 0.08)' }}
                    onClick={onOpen}
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpen();
                      }
                    }}
                  >
                    <Icon as={Edit} boxSize={7} color="primary" />
                  </Box>
                )}
              </Stack>
            );
          })}
      </Stack>

      <Dialog.Root
        size="md"
        placement="center"
        closeOnInteractOutside={false}
        open={open}
        scrollBehavior="inside"
        onOpenChange={(e: { open: boolean }) => {
          if (!e.open) {
            onClose();
            reset();
          }
        }}
      >
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content {...serviceDialogShellProps()}>
              <ServiceDialogHeader>{t('form.title')}</ServiceDialogHeader>
              <ServiceDialogCloseTrigger />
              <form onSubmit={handleSubmit(onSubmit)}>
                <ServiceDialogBody>
                  <Box mb={6}>
                    <Input type="password" {...formProps.old_password} />
                  </Box>
                  <Box mb={6}>
                    <Input type="password" {...formProps.password} />
                  </Box>
                  <Box>
                    <Input type="password" {...formProps.confirm_password} />
                  </Box>
                  <PasswordStrength password={watch('password')} />
                </ServiceDialogBody>
                <ServiceDialogFooter>
                  <HStack justifyContent="flex-end" gap={2} flexWrap="wrap">
                    <Button
                      variant="ghost"
                      title={t('cancel')}
                      onClick={() => {
                        onClose();
                        reset();
                      }}
                      {...DIALOG_ACTION_BUTTON_PROPS}
                    />
                    <Button
                      type="submit"
                      variant="filled"
                      title={t('form.submit')}
                      isLoading={isPasswordSubmitting}
                      {...DIALOG_ACTION_BUTTON_PROPS}
                    />
                  </HStack>
                </ServiceDialogFooter>
              </form>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </BorderedBox>
  );
};

export default AccountDetail;
